import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {useNavigation, useCurrentRoute} from 'react-navi'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../../../state'
import {Document, DocumentType} from '../../../../state/rest'
import {NotificationType, UserRole, UserToSite} from '../../../../state/state'
import {MainLayoutWithoutStretch, MainContent} from '../../../Layout/Layout'
import {GoBackHeaderTitle, HeaderRow} from '../../../Molecules/ViewComponents/ViewHeader'
import {useDocumentFolderSelect} from '../../../../config/utils'
import {Helmet} from 'react-navi-helmet-async'
import {v4 as uuid} from 'uuid'
import {Loader} from '../../../../Routes'
import {AssetData} from '../../../../state/rest'
import {
  DocumentAddingForm,
  DocumentAddingModalWindow,
  DocumentEditBottomButtons,
  DocumentTypeSelection
} from '../HelperComponents'
import SiteSelectionCheckboxStyle from '../../../../Components/Organisms/SiteSelectionCheckboxStyle'
import {Baseline, Grid, InvisibleContainer} from '../../../../Components/Layout/Grid'
import styled from 'styled-components'

const AdjacentFieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.9fr);
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const HeaderRowWithGoBack = ({t, isEditing, onBack}: any) => {
  return (
    <HeaderRow>
      <GoBackHeaderTitle
        label={
          isEditing
            ? `${t('documents:labels.editDocument', 'Edit document')}`
            : `${t('documents:labels.addDocument', 'Add document')}`
        }
        backLabel={t('documents:labels.documentsTitle', 'Documents')}
        onBack={onBack}
      />
    </HeaderRow>
  )
}

interface EditDocumentProps {
  id?: string
}

type DocumentSavePayload = Partial<Document> & {
  accessType: 'ChainWide' | 'SiteSpecific'
  sites: {documentId: string; siteId: string}[]
}

const EditDocument = ({id}: EditDocumentProps) => {
  const nav = useNavigation()
  const currentRoute = useCurrentRoute()
  const {state, actions} = useAppState()
  const {t} = useTranslation('documents')
  const currentState = state.me!
  const chain = state.chainsById[state.selectedChainId!]

  const writableIds = useMemo(
    () => currentState.accessRights.write.sites.concat(...currentState.accessRights.write.chains),
    [currentState.accessRights.write.sites, currentState.accessRights.write.chains]
  )

  const internalDocumentFolders: any = state.v1.documents.internalDocumentFolders

  const initialDocument = {
    type: undefined,
    name: '',
    documentFolder: {
      id: internalDocumentFolders.length > 0 ? internalDocumentFolders[0].id : ''
    },
    asset: undefined,
    url: undefined,
    sites: []
  }

  const [document, setDocument] = useState<Partial<Document>>(initialDocument)
  const [isChainWide, setIsChainWide] = useState(false)
  const [selectedSites, setSelectedSites] = useState([] as string[])
  const [showUploader, setShowUploader] = useState(false)

  const {selectMethod, selectedDocumentFolder, folderOptions, setSelected} = useDocumentFolderSelect(
    document.documentFolder!.id!
  )

  // Fetch document folders when rendering. Fetch document if id is provided
  const isLoading = state.v1.documents.documentFoldersPending

  useEffect(() => {
    const setup = async () => {
      // Fetch document if updating existing one
      if (id) {
        const document = await actions.v1.documents.getDocument(id)
        setDocument(document)
        setIsChainWide(document.sites!.length === 0)
        setSelectedSites(
          document.sites!.length === 0
            ? writableIds
            : document.sites!.map(s => s.siteId).filter(siteId => writableIds.includes(siteId))
        )
        if (document.documentFolder) {
          setSelected(document.documentFolder as any)
        }
      }
      actions.v1.documents.getDocumentFolders()
    }
    setup()
  }, [actions.v1.documents, id, setSelected, writableIds])

  useEffect(() => {
    if (!id && currentRoute.url.query?.type === 'external') {
      setDocument(current => ({...current, type: DocumentType.External, asset: undefined}))
    }
  }, [currentRoute.url.query?.type, id])

  const isEditing = !!id

  const goBack = (openId?: string | null) => {
    nav.navigate(openId ? `/documents?open=${openId}` : '/documents')
  }

  const handleCreate = () => {
    const documentToBeSent = {
      ...document,
      createdBy: state.me!.user,
      documentFolder: selectedDocumentFolder,
      accessType: isChainWide ? 'ChainWide' : 'SiteSpecific',
      sites: selectedSites.map(siteId => ({
        documentId: '',
        siteId: siteId
      }))
    } as DocumentSavePayload
    actions.v1.documents
      .createDocument(documentToBeSent)
      .then((created: any) => goBack(created?.id))
      .catch(() =>
        actions.addNotification({
          id: uuid(),
          type: NotificationType.ERROR,
          title: t('documents:msg.createDocumentError', 'Could not create document'),
          description: t('documents:msg.createDocumentErrorDescription', ''),
          visible: true,
          hideAfterDelay: 6000
        })
      )
  }

  const handleUpdate = () => {
    const documentToBeSent = {
      ...document,
      id: id!,
      type: document.type!,
      name: document.name!,
      url: document.url,
      createdAt: new Date().toISOString(),
      createdBy: state.me!.user,
      documentFolder: selectedDocumentFolder,
      accessType: isChainWide ? 'ChainWide' : 'SiteSpecific',
      sites: selectedSites.map(siteId => ({
        documentId: '',
        siteId: siteId
      }))
    } as any
    actions.v1.documents
      .updateDocument(documentToBeSent)
      .catch(() =>
        actions.addNotification({
          id: uuid(),
          type: NotificationType.ERROR,
          title: t('documents:msg.updateDocumentError', 'Could not update document'),
          description: t('documents:msg.updateDocumentErrorDescription', ''),
          visible: true,
          hideAfterDelay: 6000
        })
      )
      .then(() => goBack(id))
  }

  const handleBack = () => {
    goBack(id)
  }

  const handleCancel = () => {
    goBack(isEditing ? id : undefined)
  }

  const handleSelectType = (type: DocumentType) => {
    setDocument(() => ({...document, type}))
  }

  const handleAddAsset = (asset?: AssetData) => {
    if (asset) {
      setDocument(d => ({...document, type: DocumentType.Internal, name: d.name || asset.name, asset}))
      setShowUploader(!showUploader)
    }
  }

  const handleAddAssets = (assets: AssetData[]) => {
    setShowUploader(false)
    if (!assets.length) {
      return
    }
    // The uploaded assets are stored in shared state so the follow-up route can
    // populate its form without serializing file metadata into the URL.
    actions.v1.documents.setPendingUploadedAssets(assets)
    nav.navigate('/documents/add-files')
  }

  const isValid = useCallback((): boolean => {
    const hasName = !!document.name && document.name.trim().length > 0
    const hasFolder = !!document.documentFolder
    const hasSelectedSites = selectedSites.length > 0

    if (document.type === DocumentType.Internal) {
      const hasAsset = !!document.asset
      return hasName && hasAsset && hasFolder && hasSelectedSites
    } else if (document.type === DocumentType.External) {
      const url = document.url || ''
      const hasUrl = url.trim().length > 0 && !!url.match(/^https?:\/\/.+/)
      return hasName && hasUrl && hasFolder && hasSelectedSites
    }
    return false
  }, [document.type, document.name, document.asset, document.url, document.documentFolder, selectedSites.length])

  const isDocumentOwner = useCallback((): boolean => {
    if (!document.createdBy) return true // new file

    if (!state.me) {
      return false
    }
    const managerRoleRank: Record<string, number> = {CM: 5, BM: 4, GM: 3, LM: 2, SM: 1}
    const managerScope = (assignment: UserToSite) => {
      switch (assignment.userRole as UserRole) {
        case 'CM':
          return assignment.chainId
        case 'BM':
          return assignment.brandId
        case 'GM':
          return assignment.locationGroupId
        case 'LM':
          return assignment.locationId
        case 'SM':
          return assignment.siteId
        default:
          return undefined
      }
    }
    const creatorAssignments = document.createdBy.userToSite || []
    const creatorRole =
      document.createdByRole ||
      [...creatorAssignments].sort(
        (left, right) => (managerRoleRank[right.userRole] || 0) - (managerRoleRank[left.userRole] || 0)
      )[0]?.userRole
    const creatorRank = creatorRole ? managerRoleRank[creatorRole] : undefined
    const canManageCreator =
      creatorRole !== undefined &&
      creatorRank !== undefined &&
      state.me.user.userToSite.some(currentAssignment => {
        const currentRank = managerRoleRank[currentAssignment.userRole]
        if (currentRank === undefined || currentRank < creatorRank) return false
        if (currentRank > creatorRank) return true
        return (
          currentAssignment.userRole === creatorRole &&
          creatorAssignments.some(
            creatorAssignment =>
              creatorAssignment.userRole === creatorRole &&
              managerScope(currentAssignment) === managerScope(creatorAssignment)
          )
        )
      })
    const hasSiteScope = document.sites?.some(site => state.me!.accessRights.write.sites.includes(site.siteId))
    return (
      state.me.accessRights.superuser === true ||
      state.me.accessRights.write.chains.length > 0 ||
      Boolean(canManageCreator && hasSiteScope)
    )
  }, [state, document.createdBy, document.createdByRole, document.sites])

  return (
    <MainLayoutWithoutStretch>
      <Loader show={isLoading} />
      {isEditing ? (
        <Helmet title={t('common:routes.editDocument', 'Edit document')} />
      ) : (
        <Helmet title={t('common:routes.createDocument', 'Create document')} />
      )}
      {!isLoading && (
        <MainContent variant="white">
          <HeaderRowWithGoBack t={t} isEditing={isEditing} onBack={handleBack} />
          <InvisibleContainer>
            <Grid>
              <Baseline>
                <DocumentTypeSelection
                  isEditing={isEditing}
                  document={document}
                  t={t}
                  showUploader={showUploader}
                  toggleShowUploader={setShowUploader}
                  handleSelectType={handleSelectType}
                />
                <DocumentAddingModalWindow
                  t={t}
                  showUploader={showUploader}
                  toggleShowUploader={setShowUploader}
                  handleAddAsset={handleAddAsset}
                  handleAddAssets={handleAddAssets}
                  multiple={!isEditing}
                  maxFiles={20}
                />
                <DocumentAddingForm
                  isInstructions={false}
                  document={document}
                  setDocument={setDocument}
                  isEditing={isEditing}
                  isDocumentOwner={isDocumentOwner}
                  folderOptions={folderOptions}
                  selectedDocumentFolder={selectedDocumentFolder}
                  selectMethod={selectMethod}
                  internalDocumentFolders={internalDocumentFolders}
                  t={t}
                  fieldRowComponent={isEditing ? undefined : AdjacentFieldRow}
                  hideOriginalName
                />
              </Baseline>
              <div className="empty"></div>
            </Grid>
            <DocumentEditBottomButtons
              isEditing={isEditing}
              isDocumentOwner={isDocumentOwner}
              isValid={isValid}
              handleUpdate={handleUpdate}
              handleCreate={handleCreate}
              handleCancel={handleCancel}
              setModalOpen={() => undefined}
              showDeleteAction={false}
              t={t}
            />
          </InvisibleContainer>
        </MainContent>
      )}
      {document.type ? (
        <SiteSelectionCheckboxStyle
          infoText={t('documents:messages.selectSites', 'Select sites to which this document should be visible.')}
          writableIds={writableIds}
          chain={chain}
          isChainWide={isChainWide}
          setIsChainWide={setIsChainWide}
          selectedSites={selectedSites}
          setSelectedSites={setSelectedSites}
          isLoading={isLoading}
          t={t}
        />
      ) : null}
    </MainLayoutWithoutStretch>
  )
}

export default EditDocument
